import { useQuery } from '@tanstack/react-query';
import { Database, Bell, Shield, Activity } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

export const SystemWidget = () => {
  const { data: systemData, isLoading } = useQuery({
    queryKey: ['system-widget'],
    queryFn: async () => {
      // Get notifications count
      const { data: notifications } = await supabase
        .from('notifications')
        .select('id, read')
        .eq('read', false);

      // Get device tokens count (active devices)
      const { data: devices } = await supabase
        .from('device_tokens')
        .select('id');

      // Get recent backups
      const { data: backups } = await supabase
        .from('schedule_backups')
        .select('id, created_at')
        .order('created_at', { ascending: false })
        .limit(1);

      // Get active clock-ins (system activity)
      const { data: activeClockIns } = await supabase
        .from('clock_in_out')
        .select('id')
        .eq('status', 'active');

      const lastBackup = backups?.[0];
      const lastBackupTime = lastBackup ? 
        Math.floor((Date.now() - new Date(lastBackup.created_at).getTime()) / (1000 * 60)) : null;

      // Calculate system health score
      const unreadNotifications = notifications?.length || 0;
      const activeDevices = devices?.length || 0;
      const activeSessionsCount = activeClockIns?.length || 0;
      
      let healthScore = 100;
      if (unreadNotifications > 10) healthScore -= 20;
      if (!lastBackup || lastBackupTime > 1440) healthScore -= 30; // No backup in 24h
      if (activeDevices === 0) healthScore -= 20;

      return {
        healthScore: Math.max(0, healthScore),
        unreadNotifications,
        activeDevices,
        activeSessions: activeSessionsCount,
        lastBackupMinutes: lastBackupTime,
        backupsCount: backups?.length || 0
      };
    },
    refetchInterval: 30000,
  });

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getHealthStatus = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Poor';
  };

  if (isLoading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-32 bg-muted rounded"></div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <Activity className="w-6 h-6 text-emerald-500" />
        <h3 className="text-lg font-semibold">System Health</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className={`text-2xl font-bold ${getHealthColor(systemData?.healthScore || 0)}`}>
            {systemData?.healthScore || 0}%
          </div>
          <div className="text-sm text-muted-foreground">
            {getHealthStatus(systemData?.healthScore || 0)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{systemData?.activeDevices || 0}</div>
          <div className="text-sm text-muted-foreground">Active Devices</div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-500" />
            <span className="text-sm">Unread Notifications</span>
          </div>
          <Badge variant={systemData?.unreadNotifications ? "destructive" : "secondary"}>
            {systemData?.unreadNotifications || 0}
          </Badge>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-green-500" />
            <span className="text-sm">Active Sessions</span>
          </div>
          <Badge variant="default">{systemData?.activeSessions || 0}</Badge>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-blue-500" />
            <span className="text-sm">Last Backup</span>
          </div>
          <Badge variant={
            !systemData?.lastBackupMinutes ? "destructive" :
            systemData.lastBackupMinutes > 1440 ? "destructive" :
            systemData.lastBackupMinutes > 60 ? "outline" : "default"
          }>
            {systemData?.lastBackupMinutes ? `${systemData.lastBackupMinutes}m ago` : 'Never'}
          </Badge>
        </div>
      </div>
    </Card>
  );
};